/**
 * The five MVP cabinets (design/machines.md), built from parts.ts so that five very
 * different machines still read as one arcade.
 *
 * Silhouette contract — squint and you must still know which machine you are looking at:
 *   PACHINKO      tall narrow slab, glass belly full of pegs, launcher knob bottom-right
 *   MÜNZSCHIEBER  wide low vitrine, the pile visible from the aisle (temptation as level design)
 *   BANDIT        rounded crown + THE LEVER sticking out of the right hip
 *   ROULETTE      the only round thing in the room, waist-high, lit from below
 *   GREIFER       a glass cube with a spider in it, and a bear that will not fit the chute
 */

import {
  Group, Mesh, InstancedMesh, BoxGeometry, PlaneGeometry, CylinderGeometry, SphereGeometry,
  TorusGeometry, MeshBasicMaterial, Matrix4, Vector3, Color, CanvasTexture, SRGBColorSpace,
  RepeatWrapping, DoubleSide, AdditiveBlending, LatheGeometry, Vector2,
  InstancedBufferAttribute, type Object3D,
} from 'three';
import type { MachineId } from '../../../shared/contract';
import {
  chassis, chamferBox, cavity, glass, marquee, hausregelnBoard, topfGauge, tiltBubble,
  coinTray, buttonRow, coinSlot, legs, stickers, lightPool, contactShadow, type MarqueeOut, type Gauge,
  type RuleLineLike,
} from './parts';
import {
  MACHINE_LOOK, MATERIALS, NACHT, BAKELIT, CREME, SENF, BLUTROT, FILZ, PAPIER, INK,
  NEON, SEEGRUEN, GUMMI, css, mixHex,
} from '../art/palette';
import { toon, toonChassis, toonMetal, toonFelt, toonPlush, toonPlastic, toonCavity } from '../art/toon';
import { grime, label, printed, FONT_SIGN, FONT_MONO, halo, rng } from '../art/textures';
import { inkGroup, inkOutlineInstanced, computeHullNormals } from '../art/outline';
import { decalPlate, decalSerial } from '../art/atlas';
import { bakeStatic } from './bake';

export interface CabinetAnchors {
  /** where a payout fountain is born */
  payout: Vector3;
  /** the coin tray mouth */
  tray: Vector3;
  /** the Hausregeln board centre (camera 'board' mode racks onto this) */
  board: Vector3;
  /** the play surface the 'cabinet' camera dollies to */
  face: Vector3;
  /** where the ticket strip comes out */
  ticket: Vector3;
  /** the structural failure point — where the glass bursts on Bank sprengen */
  burst: Vector3;
}

export interface Cabinet {
  id: MachineId;
  group: Group;
  size: [number, number, number];
  marquee: MarqueeOut;
  topf: Gauge;
  tilt: Gauge;
  anchors: CabinetAnchors;
  /**
   * **Where the sim's cabinet-local origin sits in THIS model.**
   *
   * The sim and the art are two independent authorings of the same machine and they do not
   * have to agree about where the floor of the playfield is — core builds its physics in
   * whatever frame the physics wants, the art is built in whatever frame the silhouette
   * wants. `CabinetState.origin` reconciles the two in the *aisle* (which cabinet is where),
   * and this reconciles them *inside* one cabinet.
   *
   * It is (0,0,0) for machines whose two authorings happen to coincide, and it is not
   * optional for the ones where they do not: the MÜNZSCHIEBER's shelf is at y≈0 in the sim
   * and at y≈16.95 in this model, so without it the pusher's 300-coin hoard — "the player's
   * savings account", the run's signature memory — is drawn in a heap on the lino under the
   * cabinet, and the vitrine you are supposed to be tempted by is empty.
   */
  tokenMount: Vector3;
  /** per-machine animation handles, used by anim/effects.ts */
  api: Record<string, ((...a: never[]) => void) | unknown>;
  update(dt: number, t: number): void;
  setBroken(v: boolean): void;
  /**
   * Level of detail, by draw-call cost (ART-BIBLE §9):
   *   2 near / active — everything
   *   1 aisle mid     — drops gauges, glass streak, marquee spill
   *   0 far           — silhouette, marquee panel and floor pool only
   */
  setDetail(level: 0 | 1 | 2): void;
  drawCost(level: 0 | 1 | 2): number;
}

const _m4 = new Matrix4();

// ---------------------------------------------------------------- helpers

function canvasTex(w: number, h: number, draw: (g: CanvasRenderingContext2D, w: number, h: number) => void, repeat?: [number, number]): CanvasTexture {
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  draw(c.getContext('2d')!, w, h);
  const t = printed(new CanvasTexture(c));
  t.colorSpace = SRGBColorSpace;
  if (repeat) { t.wrapS = t.wrapT = RepeatWrapping; t.repeat.set(repeat[0], repeat[1]); }
  return t;
}

/** shared trim: the cream band with the machine's serial number that every cabinet wears */
function serialPlate(text: string): Mesh {
  return decalSerial(text, 3.2);
}

function baseRules(machine: MachineId): RuleLineLike[] {
  const R: Record<MachineId, RuleLineLike[]> = {
    pachinko: [
      { text: 'Einsatz: 1 Marke je Wurf', source: 'hausregel', discovered: true },
      { text: 'Mitteltasche speist DEN TOPF', source: 'hausregel', discovered: true },
      { text: 'Goldene Stifte zahlen doppelt', source: 'hausregel', discovered: true },
      { text: '[Glücks]: Taschen 20% weiter', source: 'enchant', discovered: true },
      { text: 'Magnetfalle an den Taschen', source: 'zinken', discovered: false },
    ],
    muenzschieber: [
      { text: 'Der Haufen bleibt über Nacht', source: 'hausregel', discovered: true },
      { text: 'Seitlicher Abgang: Haus kassiert', source: 'hausregel', discovered: true },
      { text: 'Nachlegen beschleunigt den Schieber', source: 'hausregel', discovered: true },
      { text: 'Zinsmünzen reifen im Haufen', source: 'enchant', discovered: true },
    ],
    bandit: [
      { text: 'Walzen ziehen aus DEM BEUTEL', source: 'hausregel', discovered: true },
      { text: '[Spalt] zählt als zwei Symbole', source: 'enchant', discovered: true },
      { text: '[Geist] darf zwischen Rasten halten', source: 'enchant', discovered: true },
      { text: '[Kleber] hält die Position', source: 'enchant', discovered: true },
      { text: 'Gezinkte Walze: dritte Rolle', source: 'zinken', discovered: true },
    ],
    roulette: [
      { text: 'Eine Marke wird zur Kugel', source: 'hausregel', discovered: true },
      { text: 'Einsätze bleiben liegen (Zins)', source: 'hausregel', discovered: true },
      { text: '[Magnet]: Kugel sucht Ihre Felder', source: 'enchant', discovered: true },
      { text: 'Gewichtete Kugel', source: 'zinken', discovered: false },
    ],
    greifer: [
      { text: 'Ein Griff je Marke', source: 'hausregel', discovered: true },
      { text: 'Doppelt oder fallen lassen', source: 'hausregel', discovered: true },
      { text: 'Der Bär passt nicht durch den Schacht', source: 'hausregel', discovered: true },
      { text: 'Müder Greifer', source: 'zinken', discovered: true },
    ],
    flipper: [
      { text: 'Der Saalchef spielt mit eigenem Beutel', source: 'hausregel', discovered: true },
      { text: 'Fünf Ziele — ein Bankett', source: 'hausregel', discovered: true },
      { text: '[Kleber]: die Marke bleibt am Schläger liegen', source: 'enchant', discovered: true },
      { text: '[Glücks]: die Verlustrinnen verengen sich', source: 'enchant', discovered: true },
      { text: 'Er gibt GLÜCK gegen Sie aus', source: 'zinken', discovered: true },
    ],
  };
  return R[machine];
}

/** the standard cabinet dressing: board, gauges, tray, slot, legs, pool, stickers */
function dress(g: Group, machine: MachineId, w: number, h: number, d: number, opts: {
  boardPos: Vector3; boardSize?: [number, number]; boardRot?: number;
  topfPos: Vector3; tiltPos: Vector3; trayPos: Vector3; trayW?: number;
  slotPos?: Vector3; poolRadius?: number;
}): { topf: Gauge; tilt: Gauge } {
  const look = MACHINE_LOOK[machine];

  const bw = opts.boardSize?.[0] ?? 6.4, bh = opts.boardSize?.[1] ?? 5.0;
  const board = hausregelnBoard(bw, bh, baseRules(machine));
  board.position.copy(opts.boardPos);
  if (opts.boardRot !== undefined) board.rotation.y = opts.boardRot;
  board.name = 'hausregeln';
  g.add(board);

  const topf = topfGauge(5.2);
  topf.group.position.copy(opts.topfPos);
  topf.set(0.35);
  g.add(topf.group);

  const tilt = tiltBubble();
  tilt.group.position.copy(opts.tiltPos);
  tilt.set(0.15);
  g.add(tilt.group);

  const tray = coinTray(opts.trayW ?? w * 0.55);
  tray.position.copy(opts.trayPos);
  g.add(tray);

  if (opts.slotPos) {
    const cs = coinSlot();
    cs.position.copy(opts.slotPos);
    g.add(cs);
  }

  g.add(legs(w, d));

  const pl = lightPool(look.accent, opts.poolRadius ?? Math.max(w, d) * 1.15, 0.30);
  pl.position.set(0, 0.06, d * 0.25);
  const cs = contactShadow(w * 1.55, d * 1.75, 0.62);
  g.add(cs, pl);

  void h;
  return { topf, tilt };
}

/**
 * Bake, ink, then sort every remaining mesh into a detail tier so the aisle can afford
 * seven of these at once. Called by every builder as the last thing it does.
 */
function finish(c: Cabinet): Cabinet {
  const bake = bakeStatic(c.group, 2.4);
  inkGroup(c.group, 2.4);
  c.group.userData.bake = bake;

  const tier1: Object3D[] = [];   // dropped at detail 1 and below
  const tier2: Object3D[] = [];   // dropped at detail 0
  const effTier = (o: Object3D): number | undefined => {
    for (let p: Object3D | null = o; p && p !== c.group; p = p.parent) {
      if (typeof p.userData.tier === 'number') return p.userData.tier as number;
    }
    return undefined;
  };
  c.group.traverse((o) => {
    const m = o as Mesh;
    if (!m.isMesh) return;
    const t = effTier(m);
    if (t === 1) { tier1.push(m); return; }
    if (t === 2) { tier2.push(m); return; }
    const mat = m.material as { name?: string } | undefined;
    if (mat?.name === 'decals') { tier2.push(m); return; }
  });

  const counts: [number, number, number] = [0, 0, 0];
  c.group.traverse((o) => { if ((o as Mesh).isMesh || (o as { isLine?: boolean }).isLine) counts[2]++; });
  counts[1] = counts[2] - tier1.length;
  counts[0] = counts[1] - tier2.length;

  c.setDetail = (level) => {
    for (const o of tier1) o.visible = level >= 2;
    for (const o of tier2) o.visible = level >= 1;
  };
  c.drawCost = (level) => counts[level];
  return c;
}

// ================================================================ PACHINKO

export function buildPachinko(): Cabinet {
  const g = new Group();
  g.name = 'pachinko';
  const W = 14, H = 26, D = 7;
  const look = MACHINE_LOOK.pachinko;

  const body = chassis(W, H, D, look.chassis, { seed: 2, wear: [[0.78, 0.18, 0.16]] });
  body.position.y = H / 2 + 1.6;
  g.add(body);

  // the glass belly: a recessed dark field with pegs
  const fieldW = 10.4, fieldH = 15.5;
  const fieldY = 1.6 + H * 0.60;
  const FZ = D / 2;                       // the cabinet's front face
  // the field is a recess PROUD of the face: a dark box with a bezel frame around it
  const back = cavity(fieldW, fieldH, 0.5);
  back.position.set(0, fieldY, FZ + 0.30);
  g.add(back);
  {
    const bez = toonChassis(mixHex(look.chassis.shade, BAKELIT.shade, 0.45), grime({ seed: 3 }));
    const bars: [number, number, number, number, number][] = [
      [0, fieldY + fieldH / 2 + 0.55, fieldW + 2.2, 1.1, 1.5],
      [0, fieldY - fieldH / 2 - 0.55, fieldW + 2.2, 1.1, 1.5],
      [-fieldW / 2 - 0.55, fieldY, 1.1, fieldH + 2.2, 1.5],
      [fieldW / 2 + 0.55, fieldY, 1.1, fieldH + 2.2, 1.5],
    ];
    for (const [bx, by, bw, bh, bd] of bars) {
      const bar = new Mesh(chamferBox(bw, bh, bd, 0.16), bez);
      bar.position.set(bx, by, FZ + 0.42);
      bar.userData.ink = 2.2; bar.userData.warmInk = true;
      bar.castShadow = true;
      g.add(bar);
    }
  }

  // the peg field — one InstancedMesh + one ink hull for ~110 pegs
  const pegGeo = new CylinderGeometry(0.16, 0.16, 0.7, 7);
  pegGeo.rotateX(Math.PI / 2);
  computeHullNormals(pegGeo);
  const pegMat = toonMetal(MATERIALS.silber.base);
  const rows = 11, cols = 10;
  const pegs = new InstancedMesh(pegGeo, pegMat, rows * cols);
  pegs.userData.noInk = true;
  let n = 0;
  const rnd = rng(19);
  const golden: number[] = [];
  for (let r = 0; r < rows; r++) {
    for (let cIdx = 0; cIdx < cols; cIdx++) {
      const stagger = (r % 2) * 0.5;
      const x = (cIdx + stagger - (cols - 1) / 2) * (fieldW / cols);
      const y = fieldY + fieldH / 2 - 2.2 - r * ((fieldH - 3.4) / rows);
      if (Math.abs(x) > fieldW / 2 - 0.5) continue;
      _m4.makeTranslation(x, y, FZ + 0.62);
      pegs.setMatrixAt(n, _m4);
      if (rnd() < 0.09) golden.push(n);
      n++;
    }
  }
  pegs.count = n;
  pegs.instanceMatrix.needsUpdate = true;
  const pegInk = inkOutlineInstanced(pegs, { widthPx: 1.4, body: MATERIALS.silber.base });
  g.add(pegInk, pegs);

  // golden pegs (Nachlegen adds these) — a few emissive studs
  const goldGeo = new CylinderGeometry(0.21, 0.21, 0.72, 8);
  goldGeo.rotateX(Math.PI / 2);
  const goldMat = toon({ color: MATERIALS.gold.base, ramp: 'METAL4', rim: 0.7, emissive: 0x352200 });
  const goldPegs = new InstancedMesh(goldGeo, goldMat, Math.max(1, golden.length));
  goldPegs.userData.noInk = true;
  goldPegs.userData.dynamic = true;
  golden.forEach((idx, i) => {
    pegs.getMatrixAt(idx, _m4);
    goldPegs.setMatrixAt(i, _m4);
  });
  goldPegs.count = golden.length;
  goldPegs.instanceMatrix.needsUpdate = true;
  g.add(goldPegs);

  // pockets along the bottom of the field, with the TOPF mouth in the middle
  const pocketY = fieldY - fieldH / 2 + 0.9;
  const pocketVals = ['20', '50', 'TOPF', '50', '20'];
  pocketVals.forEach((v, i) => {
    const x = (i - 2) * (fieldW / 5.2);
    const isTopf = v === 'TOPF';
    const mouth = new Mesh(chamferBox(1.85, 1.5, 0.7, 0.12),
      toonPlastic(isTopf ? SENF.base : BAKELIT.shade, 0x000000, 0.6));
    mouth.position.set(x, pocketY, FZ + 0.55);
    mouth.userData.ink = 2.0;
    const hole = cavity(1.35, 1.0, 0.5);
    hole.position.set(x, pocketY + 0.1, FZ + 0.34);
    const lbl = decalPlate(v, 1.5, { font: FONT_SIGN, weight: '600', size: 44, color: isTopf ? INK : CREME.lit });
    lbl.position.set(x, pocketY - 1.05, FZ + 1.18);
    g.add(mouth, hole, lbl);
  });

  const pane = glass(fieldW + 0.5, fieldH + 0.4);
  pane.position.set(0, fieldY, FZ + 1.02);
  g.add(pane);

  // the launcher: chrome knob + curved rail on the right hip
  const knob = new Mesh(new SphereGeometry(0.95, 14, 10), toonMetal(MATERIALS.silber.lit));
  knob.position.set(W / 2 - 1.5, 1.6 + H * 0.20, FZ + 1.5);
  knob.userData.ink = 2.2;
  knob.userData.dynamic = true;
  knob.castShadow = true;
  const knobStem = new Mesh(new CylinderGeometry(0.28, 0.28, 1.2, 10), toonMetal(MATERIALS.silber.shade));
  knobStem.rotation.x = Math.PI / 2;
  knobStem.position.set(knob.position.x, knob.position.y, FZ + 0.9);
  knobStem.userData.ink = 1.8;
  knobStem.userData.dynamic = true;
  const rail = new Mesh(new TorusGeometry(3.2, 0.16, 6, 20, Math.PI * 0.8), toonMetal(MATERIALS.silber.base));
  rail.position.set(W / 2 - 2.2, fieldY - fieldH / 2 + 1.5, FZ + 0.7);
  rail.rotation.z = -0.6;
  rail.userData.ink = 1.6;
  g.add(knob, knobStem, rail);

  const mq = marquee('KUGELREGEN', 11, 2.6, look.accent, 'PACHINKO');
  mq.group.position.set(0, 1.6 + H + 1.9, D / 2 - 0.6);
  g.add(mq.group);

  const st = stickers(W, H, 2);
  st.position.set(-W / 2 + 2.2, 1.6 + H * 0.35, D / 2 + 0.05);
  g.add(st);
  const sp = serialPlate('KR-1987/04');
  sp.position.set(W / 2 - 2.6, 1.6 + 1.2, D / 2 + 0.06);
  g.add(sp);

  const { topf, tilt } = dress(g, 'pachinko', W, H, D, {
    boardPos: new Vector3(-W / 2 - 0.35, 1.6 + H * 0.62, 0),
    boardSize: [6.0, 4.8], boardRot: -Math.PI / 2,
    topfPos: new Vector3(W / 2 + 0.4, 1.6 + H * 0.62, 0),
    tiltPos: new Vector3(-W / 2 + 1.6, 1.6 + H * 0.12, D / 2 + 0.3),
    trayPos: new Vector3(0, 1.6 + 1.0, D / 2 + 1.2),
    slotPos: new Vector3(W / 2 - 4.2, 1.6 + H * 0.115, D / 2 + 0.3),
  });

  let knobPull = 0;
  const cab: Cabinet = {
    id: 'pachinko', group: g, size: [W, H + 1.6, D], marquee: mq, topf, tilt,
    // sim field y 0..20 against art field y 9.45..24.95 — close enough that the drop reads
    // correctly without a scale, once lifted to the bottom of the glass.
    tokenMount: new Vector3(0, fieldY - fieldH / 2 - 0.4, FZ + 0.55),
    anchors: {
      payout: new Vector3(0, fieldY - fieldH / 2, D / 2 + 1.2),
      tray: new Vector3(0, 1.6 + 1.4, D / 2 + 1.6),
      board: new Vector3(-W / 2 - 1.2, 1.6 + H * 0.62, 0),
      face: new Vector3(0, fieldY, D / 2 + 1.0),
      ticket: new Vector3(-W / 2 + 2.0, 1.6 + 2.6, D / 2 + 0.9),
      burst: new Vector3(0, fieldY, D / 2 + 1.0),
    },
    api: {
      /** Setzen: wind the launcher back and let go */
      pull(v: number) { knobPull = v; },
      fieldTop: new Vector3(0, fieldY + fieldH / 2 - 1, D / 2 + 0.6),
      fieldRect: [fieldW, fieldH, fieldY] as const,
    },
    update(_dt: number, t: number) {
      knob.rotation.z = -knobPull * 1.5;
      knobStem.position.z = D / 2 + 0.9 - knobPull * 0.5;
      knob.position.z = D / 2 + 1.5 - knobPull * 0.5;
      goldMat.emissive.setHex(mixHex(0x241700, 0x6a4a00, 0.5 + 0.5 * Math.sin(t * 2.1)));
    },
    setBroken() { /* handled by the room */ },
    setDetail() { }, drawCost() { return 0; },
  };
  return finish(cab);
}

// ================================================================ MÜNZSCHIEBER

export function buildSchieber(): Cabinet {
  const g = new Group();
  g.name = 'muenzschieber';
  const W = 19, H = 15, D = 15;
  const look = MACHINE_LOOK.muenzschieber;

  const body = chassis(W, H, D, look.chassis, { seed: 5, wear: [[0.5, 0.2, 0.22]] });
  body.position.y = H / 2 + 1.6;
  g.add(body);

  // the vitrine: the pile must be visible from the aisle — temptation as level design
  const vitH = 9.5;
  const vitY = 1.6 + H + vitH / 2 - 0.6;
  const glassBox = new Group();
  const frameMat = toonChassis(BAKELIT.base, grime({ seed: 6 }));
  for (const [x, z, w2, d2] of [
    [-W / 2 + 0.3, 0, 0.6, D], [W / 2 - 0.3, 0, 0.6, D], [0, -D / 2 + 0.3, W, 0.6],
  ] as [number, number, number, number][]) {
    const post = new Mesh(chamferBox(w2, vitH, d2, 0.15), frameMat);
    post.position.set(x, vitY, z);
    post.userData.ink = 2.2; post.castShadow = true;
    glassBox.add(post);
  }
  const roof = new Mesh(chamferBox(W, 0.7, D, 0.2), frameMat);
  roof.position.set(0, vitY + vitH / 2, 0);
  roof.userData.ink = 2.2; roof.castShadow = true;
  glassBox.add(roof);
  const backWall = cavity(W - 1.2, vitH - 0.6, 0.5);
  backWall.position.set(0, vitY, -D / 2 + 0.7);
  glassBox.add(backWall);
  const front = glass(W - 1.2, vitH - 0.8);
  front.position.set(0, vitY, D / 2 + 0.05);
  glassBox.add(front);
  g.add(glassBox);

  // the two-level shelf
  const shelfMat = toonMetal(mixHex(MATERIALS.silber.base, BAKELIT.shade, 0.4));
  const lowShelf = new Mesh(new BoxGeometry(W - 1.6, 0.5, D - 4.0), shelfMat);
  lowShelf.position.set(0, vitY - vitH / 2 + 0.7, -0.6);
  lowShelf.userData.ink = 2.0; lowShelf.receiveShadow = true;
  const highShelf = new Mesh(new BoxGeometry(W - 1.6, 0.5, D * 0.42), shelfMat);
  highShelf.position.set(0, vitY - vitH / 2 + 3.1, -D * 0.27);
  highShelf.userData.ink = 2.0; highShelf.receiveShadow = true;
  g.add(lowShelf, highShelf);

  // THE PUSHER BAR — the House does not emote: this moves linearly, forever (§7.2)
  const pusher = new Mesh(chamferBox(W - 2.0, 1.5, 3.2, 0.15),
    toonPlastic(mixHex(SEEGRUEN.shade, MATERIALS.silber.base, 0.35), 0x000000, 0.8));
  pusher.position.set(0, vitY - vitH / 2 + 1.5, -2.4);
  pusher.userData.ink = 2.2;
  pusher.userData.dynamic = true;
  pusher.castShadow = true;
  g.add(pusher);

  // the front edge and the drop slot: where the money leaves
  const lip = new Mesh(new BoxGeometry(W - 1.6, 0.5, 1.0), shelfMat);
  lip.position.set(0, vitY - vitH / 2 + 0.7, D / 2 - 1.6);
  lip.userData.ink = 1.8;
  const dropSlot = cavity(W - 2.4, 1.6, 1.4);
  dropSlot.position.set(0, vitY - vitH / 2 - 0.4, D / 2 - 1.4);
  g.add(lip, dropSlot);

  // drop chutes on top: where you time your Setzen
  const chuteMat = toonMetal(MATERIALS.silber.shade).clone();
  chuteMat.side = DoubleSide;
  for (const x of [-4.5, 0, 4.5]) {
    const chute = new Mesh(new CylinderGeometry(0.5, 0.62, 0.9, 10, 1, true), chuteMat);
    chute.position.set(x, vitY + vitH / 2 - 0.7, D * 0.28);
    chute.userData.ink = 1.6;
    g.add(chute);
  }

  {
    const band = new Mesh(chamferBox(W + 0.3, 1.0, D + 0.3, 0.14), toonMetal(MATERIALS.silber.shade));
    band.position.set(0, 1.6 + H * 0.42, 0);
    band.userData.ink = 2.0;
    band.castShadow = true;
    const panel = new Mesh(chamferBox(W * 0.52, 3.4, 1.2, 0.2),
      toonChassis(mixHex(look.chassis.shade, BAKELIT.base, 0.5), grime({ seed: 6 })));
    panel.position.set(-2.0, 1.6 + H * 0.24, D / 2 + 0.4);
    panel.rotation.x = 0.18;
    panel.userData.ink = 2.2; panel.userData.warmInk = true;
    const btns = buttonRow(3, 1.9, [SENF.base, BLUTROT.base, SEEGRUEN.lit]);
    btns.position.set(-2.0, 1.6 + H * 0.24, D / 2 + 1.05);
    btns.rotation.x = 0.18;
    g.add(band, panel, btns);
  }

  const mq = marquee('MÜNZSCHIEBER', 13, 2.6, look.accent, 'GELDREGEN GARANTIERT');
  mq.group.position.set(0, vitY + vitH / 2 + 1.9, -D / 2 + 1.4);
  g.add(mq.group);

  const sp = serialPlate('MS-1985/11');
  sp.position.set(W / 2 - 3.2, 1.6 + 1.4, D / 2 + 0.06);
  g.add(sp);
  const st = stickers(W, H, 4);
  st.position.set(-W / 2 + 3.0, 1.6 + H * 0.45, D / 2 + 0.05);
  g.add(st);

  const { topf, tilt } = dress(g, 'muenzschieber', W, H, D, {
    boardPos: new Vector3(-W / 2 - 0.35, 1.6 + H * 0.60, 0),
    boardSize: [6.4, 4.6], boardRot: -Math.PI / 2,
    topfPos: new Vector3(W / 2 + 0.45, 1.6 + H * 0.55, 0),
    tiltPos: new Vector3(-W / 2 + 2.0, 1.6 + H * 0.28, D / 2 + 0.3),
    trayPos: new Vector3(0, 1.6 + 1.1, D / 2 + 1.4), trayW: W * 0.5,
    slotPos: new Vector3(W / 2 - 4.0, 1.6 + H * 0.3, D / 2 + 0.3),
  });

  let phase = 0, speed = 1;
  const cab: Cabinet = {
    id: 'muenzschieber', group: g,
    // measured: art shelf top 16.95 (vitY 20.75 − vitH/2 4.75 + 0.7 + half of 0.5),
    // sim shelf 0. Sim shelf mid-z 3.5 against art shelf z −0.6.
    tokenMount: new Vector3(0, 16.95, -4.1), size: [W, H + vitH, D], marquee: mq, topf, tilt,
    anchors: {
      payout: new Vector3(0, vitY - vitH / 2, D / 2 - 1.0),
      tray: new Vector3(0, 1.6 + 1.5, D / 2 + 1.8),
      board: new Vector3(-W / 2 - 1.2, 1.6 + H * 0.60, 0),
      face: new Vector3(0, vitY - 1.0, D / 2 + 0.4),
      ticket: new Vector3(W / 2 - 2.4, 1.6 + 3.0, D / 2 + 0.5),
      burst: new Vector3(0, vitY, D / 2 + 0.2),
    },
    api: {
      setSpeed(v: number) { speed = v; },
      shelfY: vitY - vitH / 2 + 1.0,
      shelfRect: [W - 2.2, D - 4.4] as const,
      pusherRef: pusher,
    },
    update(dt: number, _t: number) {
      // LINEAR. Deliberately. The House does not ease. (ART-BIBLE §7.2)
      phase = (phase + dt * 0.22 * speed) % 1;
      const tri = phase < 0.5 ? phase * 2 : 2 - phase * 2;
      pusher.position.z = -3.6 + tri * 3.4;
    },
    setBroken() { },
    setDetail() { }, drawCost() { return 0; },
  };
  return finish(cab);
}

// ================================================================ BANDIT

export function buildBandit(): Cabinet {
  const g = new Group();
  g.name = 'bandit';
  const W = 12, H = 22, D = 9;
  const look = MACHINE_LOOK.bandit;

  const body = chassis(W, H, D, look.chassis, { seed: 8, wear: [[0.5, 0.30, 0.17]] });
  body.position.y = H / 2 + 1.6;
  g.add(body);

  // the rounded crown — half the silhouette
  const crownPts: Vector2[] = [];
  for (let i = 0; i <= 10; i++) {
    const a = (i / 10) * Math.PI * 0.5;
    crownPts.push(new Vector2(Math.cos(a) * (W / 2), Math.sin(a) * 2.1));
  }
  const crown = new Mesh(new LatheGeometry(crownPts, 16, 0, Math.PI), toonChassis(look.chassis.base, grime({ seed: 8 })));
  crown.rotation.y = -Math.PI / 2;
  crown.scale.z = D / W;
  crown.position.y = 1.6 + H;
  crown.userData.ink = 2.4; crown.userData.warmInk = true;
  crown.castShadow = true;
  g.add(crown);

  // three reel windows
  const reelY = 1.6 + H * 0.62;
  const winW = 2.9, winH = 3.6;
  const FZ = D / 2;
  const winTotalW = winW * 3 + 1.0;
  {
    // a picture frame, so the drums are seen THROUGH it rather than in front of it
    const bez = toonMetal(MATERIALS.silber.base);
    const bars: [number, number, number, number][] = [
      [0, reelY + winH / 2 + 0.6, winTotalW + 1.6, 1.2],
      [0, reelY - winH / 2 - 0.6, winTotalW + 1.6, 1.2],
      [-winTotalW / 2 - 0.8, reelY, 1.6, winH + 2.4],
      [winTotalW / 2 + 0.8, reelY, 1.6, winH + 2.4],
      [-winW / 2 - 0.25, reelY, 0.5, winH + 0.2],
      [winW / 2 + 0.25, reelY, 0.5, winH + 0.2],
    ];
    for (const [bx, by, bw, bh] of bars) {
      const bar = new Mesh(chamferBox(bw, bh, 0.9, 0.14), bez);
      bar.position.set(bx, by, FZ + 0.55);
      bar.userData.ink = 2.0;
      bar.castShadow = true;
      g.add(bar);
    }
  }

  // On a cylinder, u runs AROUND the drum — so the reel strip is a WIDE texture.
  // The strip is the player's bag: every cell is a token face.
  const reelTex = canvasTex(1024, 160, (ctx, w, h) => {
    const cells = 8;
    const cw = w / cells;
    const cols = [MATERIALS.kupfer.base, MATERIALS.silber.base, MATERIALS.gold.base,
      MATERIALS.obsidian.base, MATERIALS.gold.base, MATERIALS.kupfer.base,
      MATERIALS.silber.base, MATERIALS.gold.lit];
    for (let i = 0; i < cells; i++) {
      const x = i * cw;
      ctx.fillStyle = css(i % 2 ? mixHex(CREME.lit, PAPIER, 0.35) : CREME.base);
      ctx.fillRect(x, 0, cw, h);
      ctx.strokeStyle = css(INK, 0.30);
      ctx.lineWidth = 4;
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke();
      ctx.beginPath();
      ctx.arc(x + cw / 2, h / 2, h * 0.33, 0, Math.PI * 2);
      ctx.fillStyle = css(cols[i % cols.length]);
      ctx.fill();
      ctx.lineWidth = 6;
      ctx.strokeStyle = css(INK, 0.85);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(x + cw / 2, h / 2, h * 0.19, 0, Math.PI * 2);
      ctx.strokeStyle = css(INK, 0.4);
      ctx.lineWidth = 4;
      ctx.stroke();
    }
  });

  const reels: Mesh[] = [];
  for (let i = 0; i < 3; i++) {
    const x = (i - 1) * (winW + 0.45);
    const drum = new Mesh(new CylinderGeometry(1.95, 1.95, winW - 0.2, 26, 1, true),
      toon({ color: 0xffffff, ramp: 'HARD3', rim: 0.35, halftone: 0.35, map: reelTex, side: DoubleSide }));
    drum.rotation.z = Math.PI / 2;
    drum.position.set(x, reelY, FZ - 1.15);
    drum.userData.noInk = true;
    drum.userData.dynamic = true;
    drum.castShadow = true;
    reels.push(drum);
    const win = cavity(winW + 0.3, winH + 0.2, 0.3);
    win.position.set(x, reelY, FZ - 3.2);
    g.add(win, drum);
  }
  const pane = glass(winTotalW + 0.4, winH + 0.3);
  pane.position.set(0, reelY, FZ + 1.05);
  g.add(pane);

  // THE LEVER — the other half of the silhouette
  const lever = new Group();
  const armMat = toonMetal(MATERIALS.silber.lit);
  const arm = new Mesh(new CylinderGeometry(0.24, 0.28, 6.2, 10), armMat);
  arm.position.y = 3.1;
  arm.userData.ink = 2.2; arm.castShadow = true;
  const ball = new Mesh(new SphereGeometry(0.95, 14, 10), toonPlastic(BLUTROT.lit, 0x2a0806, 0.3));
  ball.position.y = 6.4;
  ball.userData.ink = 2.4; ball.castShadow = true;
  const boss = new Mesh(new CylinderGeometry(0.75, 0.75, 1.0, 12), toonMetal(MATERIALS.silber.shade));
  boss.rotation.z = Math.PI / 2;
  boss.userData.ink = 2.0;
  lever.add(boss, arm, ball);
  lever.userData.dynamic = true;
  lever.position.set(W / 2 + 0.7, 1.6 + H * 0.46, 0);
  g.add(lever);

  const btns = buttonRow(3, 2.1, [SENF.base, BLUTROT.base, SENF.base]);
  btns.position.set(-1.2, 1.6 + H * 0.30, D / 2 + 0.32);
  g.add(btns);

  const mq = marquee('EINARMIGER BANDIT', 10.5, 2.4, look.accent, 'DREI WALZEN · IHR BEUTEL');
  mq.group.position.set(0, 1.6 + H + 4.3, D / 2 - 1.4);
  g.add(mq.group);

  const sp = serialPlate('EB-1983/07');
  sp.position.set(0, 1.6 + 1.3, D / 2 + 0.06);
  g.add(sp);

  const { topf, tilt } = dress(g, 'bandit', W, H, D, {
    boardPos: new Vector3(-W / 2 - 0.35, 1.6 + H * 0.60, 0),
    boardSize: [5.6, 5.0], boardRot: -Math.PI / 2,
    topfPos: new Vector3(W / 2 + 0.45, 1.6 + H * 0.72, D / 2 - 2),
    tiltPos: new Vector3(-W / 2 + 1.5, 1.6 + H * 0.22, D / 2 + 0.3),
    trayPos: new Vector3(0, 1.6 + 1.0, D / 2 + 1.3),
    slotPos: new Vector3(W / 2 - 2.4, 1.6 + H * 0.30, D / 2 + 0.3),
  });

  const spin = [0, 0, 0];
  const target = [0, 0, 0];
  const spinning = [false, false, false];
  let leverPull = 0;

  const cab: Cabinet = {
    id: 'bandit', group: g,
    // sim and art agree closely enough on this machine; no correction needed.
    tokenMount: new Vector3(0, 0, 0), size: [W, H + 5, D], marquee: mq, topf, tilt,
    anchors: {
      payout: new Vector3(0, 1.6 + 2.4, D / 2 + 0.8),
      tray: new Vector3(0, 1.6 + 1.4, D / 2 + 1.7),
      board: new Vector3(-W / 2 - 1.2, 1.6 + H * 0.60, 0),
      face: new Vector3(0, reelY, D / 2 + 1.2),
      ticket: new Vector3(W / 2 - 2.0, 1.6 + 3.0, D / 2 + 0.9),
      burst: new Vector3(0, reelY, D / 2 + 1.0),
    },
    api: {
      spinReel(i: number) { spinning[i] = true; },
      stopReel(i: number, symbol: number) {
        spinning[i] = false;
        target[i] = -(symbol % 8) * (Math.PI * 2 / 8);
      },
      pullLever(v: number) { leverPull = v; },
      reelRefs: reels,
    },
    update(dt: number, _t: number) {
      lever.rotation.x = leverPull * 0.95;
      for (let i = 0; i < 3; i++) {
        if (spinning[i]) {
          spin[i] -= dt * 14;
        } else {
          // detent: overshoot, hard clunk back, two shivers (ART-BIBLE §7.2 law 2)
          const diff = ((target[i] - spin[i] + Math.PI) % (Math.PI * 2)) - Math.PI;
          spin[i] += diff * Math.min(1, dt * 16);
        }
        reels[i].rotation.x = spin[i];
      }
    },
    setBroken() { },
    setDetail() { }, drawCost() { return 0; },
  };
  return finish(cab);
}

// ================================================================ ROULETTE

export function buildRoulette(): Cabinet {
  const g = new Group();
  g.name = 'roulette';
  const R = 9.5, H = 11;
  const look = MACHINE_LOOK.roulette;

  // pedestal
  const ped = new Mesh(new CylinderGeometry(4.0, 5.4, H - 1.0, 16),
    toonChassis(look.chassis.base, grime({ seed: 12 })));
  ped.position.y = (H - 1.0) / 2 + 1.0;
  ped.userData.ink = 2.4; ped.userData.warmInk = true;
  ped.castShadow = true;
  g.add(ped);

  // the felt table — the only large green surface in the game
  const felt = new Mesh(new CylinderGeometry(R, R, 0.6, 32), toonFelt(FILZ.base));
  felt.position.y = H;
  felt.receiveShadow = true;
  felt.userData.ink = 2.4;
  g.add(felt);

  // painted betting layout on the felt
  const feltTex = canvasTex(512, 512, (ctx, w, h) => {
    ctx.clearRect(0, 0, w, h);
    ctx.strokeStyle = css(CREME.lit, 0.65);
    ctx.lineWidth = 3;
    ctx.beginPath(); ctx.arc(w / 2, h / 2, w * 0.46, 0, Math.PI * 2); ctx.stroke();
    ctx.beginPath(); ctx.arc(w / 2, h / 2, w * 0.30, 0, Math.PI * 2); ctx.stroke();
    for (let i = 0; i < 24; i++) {
      const a = (i / 24) * Math.PI * 2;
      ctx.beginPath();
      ctx.moveTo(w / 2 + Math.cos(a) * w * 0.30, h / 2 + Math.sin(a) * w * 0.30);
      ctx.lineTo(w / 2 + Math.cos(a) * w * 0.46, h / 2 + Math.sin(a) * w * 0.46);
      ctx.stroke();
      ctx.save();
      ctx.translate(w / 2 + Math.cos(a) * w * 0.38, h / 2 + Math.sin(a) * w * 0.38);
      ctx.rotate(a + Math.PI / 2);
      ctx.fillStyle = i % 2 ? css(BLUTROT.lit, 0.8) : css(CREME.lit, 0.75);
      ctx.font = `600 26px ${FONT_SIGN}`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(String(i + 1), 0, 0);
      ctx.restore();
    }
    ctx.fillStyle = css(CREME.lit, 0.55);
    ctx.font = `600 34px ${FONT_SIGN}`;
    ctx.textAlign = 'center';
    ctx.letterSpacing = '0.2em';
    ctx.fillText('EINSATZ', w / 2, h * 0.86);
  });
  const feltPaint = new Mesh(new CylinderGeometry(R - 0.05, R - 0.05, 0.02, 32),
    new MeshBasicMaterial({ map: feltTex, transparent: true, toneMapped: false }));
  feltPaint.position.y = H + 0.32;
  feltPaint.userData.noInk = true;
  g.add(feltPaint);

  // the bowl
  const bowlPts: Vector2[] = [];
  for (let i = 0; i <= 12; i++) {
    const t = i / 12;
    bowlPts.push(new Vector2(0.6 + t * 5.4, 1.8 * Math.pow(t, 2.2)));
  }
  const bowl = new Mesh(new LatheGeometry(bowlPts, 28), toonChassis(BAKELIT.lit, grime({ seed: 13 })));
  bowl.position.y = H + 0.3;
  bowl.userData.ink = 2.4; bowl.userData.warmInk = true;
  bowl.receiveShadow = true;
  g.add(bowl);

  // the rotor: alternating red/black pockets + a brass cross
  const rotor = new Group();
  const rotorTex = canvasTex(512, 512, (ctx, w, h) => {
    ctx.fillStyle = css(BAKELIT.shade);
    ctx.fillRect(0, 0, w, h);
    const N = 37;
    for (let i = 0; i < N; i++) {
      const a0 = (i / N) * Math.PI * 2, a1 = ((i + 1) / N) * Math.PI * 2;
      ctx.beginPath();
      ctx.moveTo(w / 2, h / 2);
      ctx.arc(w / 2, h / 2, w * 0.48, a0, a1);
      ctx.closePath();
      ctx.fillStyle = i === 0 ? css(FILZ.base) : (i % 2 ? css(BLUTROT.shade) : css(NACHT.base));
      ctx.fill();
      ctx.strokeStyle = css(MATERIALS.gold.base, 0.8);
      ctx.lineWidth = 2;
      ctx.stroke();
    }
    ctx.beginPath(); ctx.arc(w / 2, h / 2, w * 0.19, 0, Math.PI * 2);
    ctx.fillStyle = css(MATERIALS.gold.base); ctx.fill();
  });
  const rotorDisc = new Mesh(new CylinderGeometry(5.1, 4.6, 0.7, 32),
    toon({ color: 0xffffff, ramp: 'HARD3', rim: 0.4, halftone: 0.4, map: rotorTex }));
  rotorDisc.userData.ink = 2.0;
  rotorDisc.receiveShadow = true;
  const cone = new Mesh(new CylinderGeometry(0.3, 1.35, 1.3, 16), toonMetal(MATERIALS.gold.base));
  cone.position.y = 0.95;
  cone.userData.ink = 1.8;
  const cross = new Mesh(new BoxGeometry(0.26, 0.26, 4.2), toonMetal(MATERIALS.gold.lit));
  cross.position.y = 1.5;
  cross.userData.ink = 1.4;
  const cross2 = cross.clone(); cross2.rotation.y = Math.PI / 2;
  rotor.add(rotorDisc, cone, cross, cross2);
  rotor.userData.dynamic = true;
  rotor.position.y = H + 0.75;
  g.add(rotor);

  // the ball
  const ball = new Mesh(new SphereGeometry(0.32, 12, 9), toonMetal(MATERIALS.silber.lit));
  ball.position.set(4.6, H + 1.4, 0);
  ball.userData.ink = 1.6;
  ball.userData.dynamic = true;
  ball.castShadow = true;
  g.add(ball);

  // hanging sign, since a table has no crown
  const mq = marquee('ROULETTE', 10, 2.4, look.accent, 'FAITES VOS JEUX');
  mq.group.position.set(0, H + 12.5, -1.5);
  g.add(mq.group);
  const hangL = new Mesh(new CylinderGeometry(0.09, 0.09, 5.2, 6), toonMetal(MATERIALS.silber.shade));
  hangL.position.set(-4.0, H + 15.4, -1.5); hangL.userData.ink = 1.4;
  const hangR = hangL.clone(); hangR.position.x = 4.0;
  g.add(hangL, hangR);

  const { topf, tilt } = dress(g, 'roulette', R * 2, H, R * 2, {
    boardPos: new Vector3(-R - 1.4, H * 0.75, 0),
    boardSize: [5.6, 4.4], boardRot: -Math.PI / 2,
    topfPos: new Vector3(R + 1.0, H * 0.62, 0),
    tiltPos: new Vector3(0, H + 0.5, R - 1.2),
    trayPos: new Vector3(0, 2.4, R - 0.4), trayW: 7,
    slotPos: new Vector3(3.4, H * 0.55, 4.6),
    poolRadius: R * 2.1,
  });

  let rotorSpin = 0.6, ballAng = 0, ballSpin = 0, ballR = 4.6, ballDrop = 0;
  const cab: Cabinet = {
    id: 'roulette', group: g,
    // sim and art agree closely enough on this machine; no correction needed.
    tokenMount: new Vector3(0, 0, 0), size: [R * 2, H + 17, R * 2], marquee: mq, topf, tilt,
    anchors: {
      payout: new Vector3(0, H + 1.2, 0),
      tray: new Vector3(0, 2.8, R + 0.4),
      board: new Vector3(-R - 2.2, H * 0.75, 0),
      face: new Vector3(0, H + 1.0, 0),
      ticket: new Vector3(-3.2, H * 0.6, R - 1.0),
      burst: new Vector3(0, H + 1.0, 0),
    },
    api: {
      launch(power: number) { ballSpin = -6.5 * power; ballR = 5.2; ballDrop = 0; rotorSpin = 1.4 * power; },
      settle(pocket: number) {
        ballDrop = 1;
        ballAng = (pocket / 37) * Math.PI * 2;
        ballSpin = 0;
      },
      ballRef: ball,
      rotorRef: rotor,
    },
    update(dt: number, _t: number) {
      rotorSpin = Math.max(0.25, rotorSpin - dt * 0.12);
      rotor.rotation.y += dt * rotorSpin;
      if (ballDrop < 1) {
        ballSpin *= 1 - dt * 0.25;
        ballAng += ballSpin * dt;
        ballR += (2.9 - ballR) * dt * (Math.abs(ballSpin) < 2.2 ? 0.9 : 0.06);
        const rattle = Math.abs(ballSpin) < 2.6 ? Math.sin(ballAng * 21) * 0.16 : 0;
        ball.position.set(Math.cos(ballAng) * ballR, H + 1.35 + Math.abs(rattle), Math.sin(ballAng) * ballR);
      } else {
        const a = ballAng + rotor.rotation.y;
        ball.position.set(Math.cos(a) * 3.7, H + 1.15, Math.sin(a) * 3.7);
      }
    },
    setBroken() { },
    setDetail() { }, drawCost() { return 0; },
  };
  return finish(cab);
}

// ================================================================ GREIFER

export function buildGreifer(): Cabinet {
  const g = new Group();
  g.name = 'greifer';
  const W = 16, H = 12, D = 14, GH = 13;
  const look = MACHINE_LOOK.greifer;

  const base = chassis(W, H, D, look.chassis, { seed: 15, wear: [[0.3, 0.55, 0.18]] });
  base.position.y = H / 2 + 1.6;
  g.add(base);

  const cabY = 1.6 + H + GH / 2;
  const frameMat = toonChassis(BAKELIT.base, grime({ seed: 16 }));
  const postGeo = chamferBox(0.7, GH, 0.7, 0.14);
  for (const [x, z] of [[-1, -1], [1, -1], [-1, 1], [1, 1]] as [number, number][]) {
    const p = new Mesh(postGeo, frameMat);
    p.position.set(x * (W / 2 - 0.35), cabY, z * (D / 2 - 0.35));
    p.userData.ink = 2.2; p.castShadow = true;
    g.add(p);
  }
  const roof = new Mesh(chamferBox(W, 0.8, D, 0.2), frameMat);
  roof.position.set(0, cabY + GH / 2, 0);
  roof.userData.ink = 2.4; roof.castShadow = true;
  g.add(roof);
  const backPanel = cavity(W - 1.4, GH - 0.8, 0.5);
  backPanel.position.set(0, cabY, -D / 2 + 0.6);
  g.add(backPanel);
  const pane = glass(W - 1.4, GH - 0.9);
  pane.position.set(0, cabY, D / 2 + 0.02);
  g.add(pane);

  // the prize floor
  const floorPlate = new Mesh(new BoxGeometry(W - 1.4, 0.4, D - 1.4), toonMetal(MATERIALS.silber.shade));
  floorPlate.position.set(0, cabY - GH / 2 + 0.4, 0);
  floorPlate.userData.ink = 1.8;
  floorPlate.receiveShadow = true;
  g.add(floorPlate);

  // wrapped prizes: instanced boxes in kitsch colours
  const prizeGeo = chamferBox(1.7, 1.7, 1.7, 0.25, 1);
  computeHullNormals(prizeGeo);
  const prizeMat = toon({ color: 0xffffff, ramp: 'HARD3', rim: 0.4, halftone: 0.6, instanced: true });
  const prizes = new InstancedMesh(prizeGeo, prizeMat, 26);
  prizes.userData.noInk = true;
  prizes.userData.dynamic = true;
  prizes.castShadow = true;
  const tints = new Float32Array(26 * 3);
  const pr = rng(31);
  const cols = [GUMMI.base, SENF.base, SEEGRUEN.base, BLUTROT.base, CREME.base];
  for (let i = 0; i < 26; i++) {
    const a = pr() * Math.PI * 2;
    const rr = pr() * (W / 2 - 2.6);
    const x = Math.cos(a) * rr, z = Math.sin(a) * rr * 0.8;
    const y = cabY - GH / 2 + 1.4 + Math.floor(pr() * 2) * 1.5;
    _m4.makeRotationY(pr() * 3);
    _m4.setPosition(x, y, z);
    prizes.setMatrixAt(i, _m4);
    const c = new Color(cols[Math.floor(pr() * cols.length)]);
    tints[i * 3] = c.r; tints[i * 3 + 1] = c.g; tints[i * 3 + 2] = c.b;
  }
  prizes.instanceMatrix.needsUpdate = true;
  prizes.geometry = prizeGeo.clone();
  prizes.geometry.setAttribute('aTint', new InstancedBufferAttribute(tints, 3));
  computeHullNormals(prizes.geometry);
  const prizeInk = inkOutlineInstanced(prizes, { widthPx: 2.0, body: SENF.base });
  prizeInk.geometry = prizes.geometry;
  g.add(prizeInk, prizes);

  // DER RIESENPLÜSCHBÄR — he does not fit the chute, and that is the joke
  const bear = buildBear();
  bear.position.set(-2.6, cabY - GH / 2 + 2.4, 1.2);
  bear.rotation.y = 0.5;
  g.add(bear);

  // the gantry + claw
  const gantry = new Group();
  const railX = new Mesh(new BoxGeometry(W - 2.2, 0.4, 0.5), toonMetal(MATERIALS.silber.base));
  railX.position.y = cabY + GH / 2 - 1.0;
  railX.userData.ink = 1.6;
  const carriage = new Group();
  const carBox = new Mesh(chamferBox(1.6, 0.9, 1.6, 0.14), toonMetal(MATERIALS.silber.lit));
  carBox.userData.ink = 1.8;
  const cable = new Mesh(new CylinderGeometry(0.055, 0.055, 4, 5), toonMetal(MATERIALS.silber.shade));
  cable.position.y = -2.2;
  cable.userData.ink = 1.2;
  const claw = new Group();
  const clawHub = new Mesh(new CylinderGeometry(0.62, 0.42, 0.7, 10), toonMetal(MATERIALS.silber.base));
  clawHub.userData.ink = 1.8;
  const fingers: Mesh[] = [];
  for (let i = 0; i < 3; i++) {
    const f = new Mesh(chamferBox(0.22, 1.9, 0.42, 0.09, 1), toonMetal(MATERIALS.silber.lit));
    f.position.set(Math.cos((i / 3) * Math.PI * 2) * 0.55, -1.0, Math.sin((i / 3) * Math.PI * 2) * 0.55);
    f.rotation.z = Math.cos((i / 3) * Math.PI * 2) * -0.35;
    f.rotation.x = Math.sin((i / 3) * Math.PI * 2) * 0.35;
    f.userData.ink = 1.8;
    fingers.push(f);
    claw.add(f);
  }
  claw.add(clawHub);
  claw.position.y = -4.2;
  carriage.add(carBox, cable, claw);
  carriage.position.set(0, cabY + GH / 2 - 1.4, 0);
  carriage.userData.dynamic = true;
  gantry.add(railX, carriage);
  g.add(gantry);

  // the chute
  const chute = new Mesh(chamferBox(3.4, 3.4, 3.0, 0.2), toonMetal(mixHex(MATERIALS.silber.base, BAKELIT.shade, 0.5)));
  chute.position.set(-W / 2 + 2.4, cabY - GH / 2 + 1.9, D / 2 - 2.0);
  chute.userData.ink = 2.0;
  const chuteHole = cavity(2.5, 2.5, 2.2);
  chuteHole.position.copy(chute.position);
  chuteHole.position.y += 0.4;
  g.add(chute, chuteHole);

  const mq = marquee('PREISTHEKE', 12, 2.6, look.accent, 'GREIFEN SIE ZU');
  mq.group.position.set(0, cabY + GH / 2 + 2.0, -D / 2 + 1.2);
  g.add(mq.group);

  const { topf, tilt } = dress(g, 'greifer', W, H, D, {
    boardPos: new Vector3(-W / 2 - 0.35, 1.6 + H * 0.55, 0),
    boardSize: [6.0, 4.4], boardRot: -Math.PI / 2,
    topfPos: new Vector3(W / 2 + 0.45, 1.6 + H * 0.55, 0),
    tiltPos: new Vector3(W / 2 - 2.4, 1.6 + H + 0.4, D / 2 - 1.0),
    trayPos: new Vector3(2.0, 1.6 + 1.1, D / 2 + 1.2), trayW: 6,
    slotPos: new Vector3(-2.0, 1.6 + H * 0.62, D / 2 + 0.3),
  });

  let cx = 0, cz = 0, drop = 0, grip = 0;
  const cab: Cabinet = {
    id: 'greifer', group: g,
    // sim and art agree closely enough on this machine; no correction needed.
    tokenMount: new Vector3(0, 0, 0), size: [W, H + GH, D], marquee: mq, topf, tilt,
    anchors: {
      payout: new Vector3(-W / 2 + 2.4, cabY - GH / 2 + 1.0, D / 2 - 1.0),
      tray: new Vector3(2.0, 1.6 + 1.5, D / 2 + 1.6),
      board: new Vector3(-W / 2 - 1.2, 1.6 + H * 0.55, 0),
      face: new Vector3(0, cabY - 1.0, D / 2 + 0.4),
      ticket: new Vector3(-2.0, 1.6 + H * 0.4, D / 2 + 0.5),
      burst: new Vector3(0, cabY, D / 2 + 0.2),
    },
    api: {
      moveClaw(x: number, z: number) { cx = x; cz = z; },
      dropClaw(v: number) { drop = v; },
      gripClaw(v: number) { grip = v; },
      bearRef: bear,
    },
    update(_dt: number, t: number) {
      carriage.position.x = cx;
      carriage.position.z = cz;
      claw.position.y = -4.2 - drop * 5.4;
      cable.scale.y = (4 + drop * 5.4) / 4;
      cable.position.y = -(4 + drop * 5.4) / 2 - 0.2;
      fingers.forEach((f, i) => {
        const a = (i / 3) * Math.PI * 2;
        const close = 0.35 - grip * 0.62;
        f.rotation.z = Math.cos(a) * -close;
        f.rotation.x = Math.sin(a) * close;
      });
      // the claw sways: the whole tension of the machine lives in this 2 degrees
      claw.rotation.z = Math.sin(t * 1.7) * 0.035 * (1 - grip);
    },
    setBroken() { },
    setDetail() { }, drawCost() { return 0; },
  };
  return finish(cab);
}

/** Der Riesenplüschbär — soft-wall prize, physics puzzle, comedy organ */
export function buildBear(scale = 1): Group {
  const g = new Group();
  const fur = toonPlush(mixHex(BAKELIT.lit, SENF.base, 0.35));
  const belly = toonPlush(mixHex(CREME.base, SENF.lit, 0.3));
  const body = new Mesh(new SphereGeometry(2.0, 14, 11), fur);
  body.scale.set(1, 1.15, 0.92);
  body.position.y = 2.0;
  body.userData.ink = 2.6; body.castShadow = true;
  const tum = new Mesh(new SphereGeometry(1.35, 12, 9), belly);
  tum.position.set(0, 1.75, 0.95);
  tum.scale.set(1, 1.05, 0.6);
  tum.userData.ink = 2.2;
  const head = new Mesh(new SphereGeometry(1.5, 14, 11), fur);
  head.position.y = 4.6;
  head.userData.ink = 2.6; head.castShadow = true;
  const snout = new Mesh(new SphereGeometry(0.72, 10, 8), belly);
  snout.position.set(0, 4.2, 1.15);
  snout.scale.set(1, 0.8, 0.8);
  snout.userData.ink = 2.0;
  const nose = new Mesh(new SphereGeometry(0.3, 8, 6), toonPlastic(NACHT.base, 0x000000, 0.3));
  nose.position.set(0, 4.28, 1.82);
  nose.userData.ink = 1.6;
  g.add(body, tum, head, snout, nose);
  for (const s of [-1, 1]) {
    const ear = new Mesh(new SphereGeometry(0.55, 10, 8), fur);
    ear.position.set(s * 1.1, 5.6, 0);
    ear.userData.ink = 2.2;
    const eye = new Mesh(new SphereGeometry(0.17, 8, 6), toonPlastic(NACHT.void, 0x000000, 0.0));
    eye.position.set(s * 0.55, 4.86, 1.42);
    eye.userData.ink = 1.2;
    const arm = new Mesh(new SphereGeometry(0.78, 10, 8), fur);
    arm.position.set(s * 2.1, 2.4, 0.35);
    arm.scale.set(0.8, 1.5, 0.8);
    arm.rotation.z = s * -0.5;
    arm.userData.ink = 2.2;
    const leg = new Mesh(new SphereGeometry(0.95, 10, 8), fur);
    leg.position.set(s * 1.15, 0.75, 0.35);
    leg.scale.set(0.85, 0.8, 1.25);
    leg.userData.ink = 2.2;
    g.add(ear, eye, arm, leg);
  }
  g.scale.setScalar(scale);
  bakeStatic(g, 2.5);
  inkGroup(g, 2.5);
  return g;
}

/**
 * DER SAALCHEF — the FLIPPER table (machines.md: bosses are pinball tables).
 *
 * Silhouette contract: a raked playfield under a tall lit backbox, the only cabinet you
 * look *down* into. Two bats at the bottom of the glass are the read at a squint — nothing
 * else in this arcade has them. Cream and violet, because it is the House's machine.
 *
 * The playfield is modelled the way the ART-BIBLE demands (§11, the most-repeated trap):
 * a dark backing **proud of** the cabinet face, a bezel frame around it, glass in front.
 * A cavity sunk into the body is invisible — the front face occludes it.
 */
export function buildFlipper(): Cabinet {
  const g = new Group();
  g.name = 'flipper';
  const W = 15, H = 25, D = 8;
  const look = MACHINE_LOOK.flipper;

  const body = chassis(W, H, D, look.chassis, { seed: 11, wear: [[0.5, 0.22, 0.2]] });
  body.position.y = H / 2 + 1.6;
  g.add(body);

  // ---- the playfield, proud of the face
  const fieldW = 11.0, fieldH = 16.0;
  const fieldY = 1.6 + H * 0.56;
  const FZ = D / 2;
  const back = cavity(fieldW, fieldH, 0.5);
  back.position.set(0, fieldY, FZ + 0.30);
  g.add(back);
  {
    const bez = toonChassis(mixHex(look.chassis.shade, BAKELIT.shade, 0.5), grime({ seed: 12 }));
    const bars: [number, number, number, number, number][] = [
      [0, fieldY + fieldH / 2 + 0.55, fieldW + 2.2, 1.1, 1.5],
      [0, fieldY - fieldH / 2 - 0.55, fieldW + 2.2, 1.1, 1.5],
      [-fieldW / 2 - 0.55, fieldY, 1.1, fieldH + 2.2, 1.5],
      [fieldW / 2 + 0.55, fieldY, 1.1, fieldH + 2.2, 1.5],
    ];
    for (const [bx, by, bw, bh, bd] of bars) {
      const bar = new Mesh(chamferBox(bw, bh, bd, 0.16), bez);
      bar.position.set(bx, by, FZ + 0.42);
      bar.userData.ink = 2.2; bar.userData.warmInk = true;
      bar.castShadow = true;
      g.add(bar);
    }
  }

  // ---- the felt-green playfield card the toys sit on
  const cardMat = toonFelt(FILZ.base);
  const card = new Mesh(new PlaneGeometry(fieldW - 0.4, fieldH - 0.4), cardMat);
  card.position.set(0, fieldY, FZ + 0.42);
  card.userData.noInk = true;
  g.add(card);

  // ---- bumpers (Pilze): the things that give the ball its energy back
  const BUMPERS: [number, number][] = [[-2.4, 3.0], [0.4, 5.0], [2.5, 2.3], [-0.8, 0.8]];
  const bumperMat = toonPlastic(NEON.violet.core, NEON.violet.core, 0.35);
  for (const [bx, by] of BUMPERS) {
    const cap = new Mesh(new CylinderGeometry(0.95, 1.15, 0.55, 12), bumperMat);
    cap.rotation.x = Math.PI / 2;
    cap.position.set(bx, fieldY + by, FZ + 0.72);
    cap.userData.ink = 2.0;
    cap.userData.dynamic = true;
    const skirt = new Mesh(new CylinderGeometry(1.25, 1.25, 0.18, 12), toonMetal(MATERIALS.silber.base));
    skirt.rotation.x = Math.PI / 2;
    skirt.position.set(bx, fieldY + by, FZ + 0.5);
    g.add(skirt, cap);
  }

  // ---- the drop-target bank: five of them, and clearing all five is the bankett
  const targetMeshes: Mesh[] = [];
  const TARGET_X = [-3.7, -2.35, -1.0, 0.35, 1.7];
  for (let i = 0; i < TARGET_X.length; i++) {
    const t = new Mesh(chamferBox(1.0, 0.85, 0.42, 0.08), toonPlastic(SENF.base, SENF.lit, 0.5));
    t.position.set(TARGET_X[i]!, fieldY + 6.6, FZ + 0.66);
    t.userData.ink = 1.8;
    t.userData.dynamic = true;
    g.add(t);
    targetMeshes.push(t);
  }

  // ---- the two bats. THE silhouette read of this cabinet.
  const batMat = toonPlastic(BLUTROT.base, BLUTROT.lit, 0.55);
  const bats: Group[] = [];
  for (const s of [-1, 1]) {
    const pivot = new Group();
    pivot.position.set(s * 2.4, fieldY - fieldH / 2 + 3.0, FZ + 0.7);
    const bat = new Mesh(chamferBox(2.6, 0.62, 0.5, 0.14), batMat);
    // the bat hangs off its pivot, so rotating the pivot sweeps it like a real flipper
    bat.position.x = s * 1.3;
    bat.userData.ink = 2.2;
    bat.userData.dynamic = true;
    pivot.add(bat);
    pivot.userData.dynamic = true;
    g.add(pivot);
    bats.push(pivot);
  }

  // ---- the drain, drawn so the threat is legible from the aisle
  const drain = cavity(2.2, 0.8, 0.4);
  drain.position.set(0, fieldY - fieldH / 2 + 0.5, FZ + 0.36);
  g.add(drain);

  const pane = glass(fieldW + 0.5, fieldH + 0.4);
  pane.position.set(0, fieldY, FZ + 1.05);
  g.add(pane);

  // ---- the backbox: the one place in the arcade that says who you are playing
  const mq = marquee('DER SAALCHEF', 12, 3.0, look.accent, 'FLIPPER · TISCHMAXIMUM');
  mq.group.position.set(0, 1.6 + H + 2.1, D / 2 - 0.6);
  g.add(mq.group);

  const st = stickers(W, H, 11);
  st.position.set(-W / 2 + 2.4, 1.6 + H * 0.30, D / 2 + 0.05);
  g.add(st);
  const sp = serialPlate('SC-1987/01');
  sp.position.set(W / 2 - 2.6, 1.6 + 1.2, D / 2 + 0.06);
  g.add(sp);

  const { topf, tilt } = dress(g, 'flipper', W, H, D, {
    boardPos: new Vector3(-W / 2 - 0.35, 1.6 + H * 0.62, 0),
    boardSize: [6.0, 4.8], boardRot: -Math.PI / 2,
    topfPos: new Vector3(W / 2 + 0.4, 1.6 + H * 0.62, 0),
    tiltPos: new Vector3(-W / 2 + 1.6, 1.6 + H * 0.12, D / 2 + 0.3),
    trayPos: new Vector3(0, 1.6 + 1.0, D / 2 + 1.2),
    slotPos: new Vector3(W / 2 - 4.2, 1.6 + H * 0.115, D / 2 + 0.3),
  });

  let angL = -0.46, angR = -0.46;
  const cab: Cabinet = {
    id: 'flipper', group: g,
    // sim and art agree closely enough on this machine; no correction needed.
    tokenMount: new Vector3(0, 0, 0), size: [W, H + 1.6, D], marquee: mq, topf, tilt,
    anchors: {
      payout: new Vector3(0, fieldY - fieldH / 2, D / 2 + 1.2),
      tray: new Vector3(0, 1.6 + 1.4, D / 2 + 1.6),
      board: new Vector3(-W / 2 - 1.2, 1.6 + H * 0.62, 0),
      face: new Vector3(0, fieldY, D / 2 + 1.0),
      ticket: new Vector3(-W / 2 + 2.0, 1.6 + 2.6, D / 2 + 0.9),
      burst: new Vector3(0, fieldY, D / 2 + 1.0),
    },
    api: {
      /** the sim's bat angles, straight onto the pivots */
      setFlippers(l: number, r: number) { angL = l; angR = r; },
      /** which drop targets are still standing */
      setTargets(down: readonly boolean[]) {
        for (let i = 0; i < targetMeshes.length; i++) {
          const m = targetMeshes[i]!;
          const isDown = down[i] === true;
          m.visible = !isDown;
        }
      },
      fieldRect: [fieldW, fieldH, fieldY] as const,
    },
    update(_dt: number, t: number) {
      // The bats mirror: the left one sweeps anticlockwise, the right one clockwise.
      bats[0]!.rotation.z = angL;
      bats[1]!.rotation.z = -angR;
      bumperMat.emissive.setHex(mixHex(0x2a0f45, NEON.violet.core, 0.35 + 0.25 * Math.sin(t * 3.3)));
    },
    setBroken(v: boolean) { mq.setIntensity(v ? 0.06 : 1); },
    setDetail(level: 0 | 1 | 2) {
      card.visible = level >= 1;
      pane.visible = level >= 2;
      for (const b of bats) b.visible = level >= 1;
      for (const m of targetMeshes) m.visible = level >= 1 && m.visible;
    },
    drawCost(level: 0 | 1 | 2) { return level === 2 ? 26 : level === 1 ? 15 : 6; },
  };

  bakeStatic(g, 2.4);
  inkGroup(g, 2.4);
  return cab;
}

export const CABINET_BUILDERS: Record<MachineId, () => Cabinet> = {
  pachinko: buildPachinko,
  muenzschieber: buildSchieber,
  bandit: buildBandit,
  roulette: buildRoulette,
  greifer: buildGreifer,
  flipper: buildFlipper,
};

export { NEON, halo, AdditiveBlending };
